import { useEffect, useState } from "react";
import { ShieldCheck, UserGear, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";

const ALL_ROLES = ["admin", "crm", "finance", "mis", "hr", "tracker", "viewer"];
const ROLE_DESC = {
  admin: "Full access + user management",
  crm: "Leads, Catalog, Proposals",
  finance: "Reyansh Finance & Salaries",
  mis: "MIS Productivity",
  hr: "HR · letters, resumes, onboarding, voucher approval",
  tracker: "Shelves-Track tasks",
  viewer: "Read-only across all",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState({}); // user_id -> Set<role>
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
    const map = {};
    data.forEach((u) => { map[u.user_id] = new Set(u.roles || []); });
    setEditing(map);
  };
  useEffect(() => { load(); }, []);

  const toggleRole = (uid, role) => {
    setEditing((prev) => {
      const next = { ...prev };
      const s = new Set(next[uid] || []);
      s.has(role) ? s.delete(role) : s.add(role);
      next[uid] = s;
      return next;
    });
  };

  const save = async (uid) => {
    const roles = Array.from(editing[uid] || []);
    setSaving(uid);
    try {
      await api.put(`/admin/users/${uid}/roles`, { roles });
      toast.success("Roles updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update roles");
    } finally { setSaving(null); }
  };

  const remove = async (uid) => {
    if (!window.confirm("Permanently delete this user?")) return;
    await api.delete(`/admin/users/${uid}`);
    toast.success("User deleted");
    load();
  };

  return (
    <Layout
      title="Users & Roles"
      subtitle={`${users.length} user${users.length === 1 ? "" : "s"} — assign roles to control what each user can access`}
    >
      <div className="border border-[#D90429]/20 bg-[#D90429]/5 p-4 mb-6 flex items-start gap-3" data-testid="admin-banner">
        <ShieldCheck size={20} weight="bold" className="text-[#D90429] mt-0.5"/>
        <div className="text-sm text-[#4A4D57]">
          You are the Reyansh administrator. New users who sign in via Google Workspace will land in <b>pending approval</b> until you assign them a role here.
        </div>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="users-table">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>
              <th className="px-4 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">User</th>
              {ALL_ROLES.map((r) => (
                <th key={r} className="px-3 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680] text-center" title={ROLE_DESC[r]}>{r}</th>
              ))}
              <th className="px-4 py-3"/>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.user_id === me?.user_id;
              const currentRoles = u.roles || [];
              const draftRoles = Array.from(editing[u.user_id] || []).sort();
              const dirty = JSON.stringify(currentRoles.sort()) !== JSON.stringify(draftRoles);
              return (
                <tr key={u.user_id} className="border-t border-black/5" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full"/> : <div className="w-9 h-9 bg-[#D90429] text-white flex items-center justify-center text-sm font-bold">{u.name?.[0]?.toUpperCase()}</div>}
                      <div>
                        <div className="font-semibold flex items-center gap-2">{u.name}{isMe && <span className="text-[9px] font-mono-shelv uppercase tracking-widest bg-[#0A0A0A] text-white px-1.5 py-0.5">You</span>}</div>
                        <div className="text-xs text-[#717680] font-mono-shelv">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  {ALL_ROLES.map((r) => (
                    <td key={r} className="px-3 py-3 text-center">
                      <Checkbox
                        checked={(editing[u.user_id] || new Set()).has(r)}
                        onCheckedChange={() => toggleRole(u.user_id, r)}
                        disabled={isMe && r === "admin"}
                        data-testid={`role-checkbox-${u.user_id}-${r}`}
                      />
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => save(u.user_id)} disabled={!dirty || saving === u.user_id} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C] disabled:bg-black/10 disabled:text-[#717680]" data-testid={`save-roles-${u.user_id}`}>
                        <FloppyDisk size={12}/> {saving === u.user_id ? "…" : "Save"}
                      </button>
                      {!isMe && (
                        <button onClick={() => remove(u.user_id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-user-${u.user_id}`}><Trash size={14}/></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-5 gap-3">
        {ALL_ROLES.map((r) => (
          <div key={r} className="border border-black/10 p-3 text-xs">
            <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#D90429] mb-1">{r}</div>
            <div className="text-[#4A4D57]">{ROLE_DESC[r]}</div>
          </div>
        ))}
      </div>

      <GmailConnectPanel />
      <ApiTokensPanel />
      <WipeDataPanel />
    </Layout>
  );
}

// ============ API TOKENS PANEL (for external integrations) ============
function ApiTokensPanel() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [days, setDays] = useState(365);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // {token, label, expires_at}

  const load = async () => {
    try {
      const { data } = await api.get("/admin/api-tokens");
      setTokens(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Give the token a label (e.g. shelvestech.com website)"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-tokens", { name: name.trim(), expires_days: Number(days) || 365 });
      setJustCreated(data);
      setName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke token «${t.label}»? Any integration using it will stop working immediately.`)) return;
    try {
      await api.delete(`/admin/api-tokens/${encodeURIComponent(t.id)}`);
      toast.success("Token revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="mt-10 border border-black/10 bg-white p-4 md:p-5" data-testid="api-tokens-panel">
      <div className="overline text-[#D90429] mb-1">🔑 API Access</div>
      <h3 className="font-display text-xl font-black tracking-tighter text-[#0A0A0A] mb-1">External integration tokens</h3>
      <p className="text-sm text-[#4A4D57] mb-4">
        Create long-lived bearer tokens to push leads / read data from external systems (your website, automation tools, BI dashboards). Each token belongs to <b>you</b> and inherits your roles.
      </p>

      {/* Download the integration kit */}
      <div className="mb-4 border border-[#0D9488]/30 bg-[#0D9488]/5 p-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <div className="font-semibold text-[#0A0A0A] text-sm">📘 Website Integration Kit (Markdown)</div>
          <div className="text-[11px] text-[#4A4D57]">Complete developer guide — base URL, auth, payload schema, code samples in Node / PHP / Python / curl. Share with your agency.</div>
        </div>
        <button
          onClick={async () => {
            try {
              const res = await api.get("/admin/integration-kit", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = "reyansh-crm-integration-kit.md";
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
              toast.success("Integration kit downloaded");
            } catch (e) { toast.error("Download failed"); }
          }}
          className="px-3 py-2 bg-[#0D9488] text-white text-xs font-semibold hover:bg-[#0F766E] inline-flex items-center gap-1.5"
          data-testid="download-integration-kit"
        >
          Download Kit
        </button>
      </div>

      {justCreated && (
        <div className="border border-[#0D9488] bg-[#0D9488]/5 p-3 mb-4" data-testid="api-token-revealed">
          <div className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#0D9488] mb-1">⚠ Token created · copy it now, you won&apos;t see it again</div>
          <div className="font-mono-shelv text-[12px] break-all bg-white p-2 border border-[#0D9488]/30 select-all">{justCreated.token}</div>
          <div className="mt-2 flex gap-2">
            <button onClick={() => copy(justCreated.token)} className="text-xs px-3 py-1 bg-[#0D9488] text-white font-semibold" data-testid="copy-new-token">Copy token</button>
            <button onClick={() => setJustCreated(null)} className="text-xs px-3 py-1 border border-black/15">Dismiss</button>
          </div>
          <div className="text-[10px] text-[#4A4D57] mt-2 font-mono-shelv">label: <b>{justCreated.label}</b> · expires: <b>{(justCreated.expires_at || "").slice(0, 10)}</b></div>
        </div>
      )}

      {/* Create form */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-4 items-end">
        <div className="sm:col-span-7">
          <label className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#4A4D57]">Token label</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. shelvestech.com lead capture" className="block w-full border border-black/15 px-3 py-2 text-sm rounded-none focus:outline-none focus:border-[#D90429]" data-testid="api-token-label-input"/>
        </div>
        <div className="sm:col-span-3">
          <label className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#4A4D57]">Expires in</label>
          <select value={days} onChange={(e) => setDays(e.target.value)} className="block w-full border border-black/15 px-3 py-2 text-sm rounded-none focus:outline-none focus:border-[#D90429]">
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
            <option value={730}>2 years</option>
            <option value={3650}>10 years</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <button onClick={create} disabled={creating || !name.trim()} className="w-full px-4 py-2 bg-[#D90429] text-white text-sm font-bold hover:bg-[#EF233C] disabled:opacity-40" data-testid="api-token-create">
            {creating ? "Creating…" : "Generate"}
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-xs text-[#717680] font-mono-shelv">Loading…</p>
      ) : tokens.length === 0 ? (
        <p className="text-xs text-[#717680] font-mono-shelv">No tokens yet. Generate one above to start integrating.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="api-tokens-table">
            <thead className="text-left text-[10px] text-[#717680] uppercase font-mono-shelv tracking-widest">
              <tr><th className="py-1.5 pr-3">Label</th><th className="py-1.5 pr-3">Token</th><th className="py-1.5 pr-3">Created</th><th className="py-1.5 pr-3">Expires</th><th></th></tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className="border-t border-black/5">
                  <td className="py-2 pr-3 font-semibold">{t.label}</td>
                  <td className="py-2 pr-3 font-mono-shelv text-[11px] text-[#4A4D57]">{t.preview}</td>
                  <td className="py-2 pr-3 font-mono-shelv text-[11px]">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="py-2 pr-3 font-mono-shelv text-[11px]">{(t.expires_at || "").slice(0, 10)}</td>
                  <td className="py-2 pr-3 text-right">
                    <button onClick={() => revoke(t)} className="text-xs px-2 py-1 border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white" data-testid={`revoke-token-${t.id}`}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mini docs */}
      <details className="mt-4 border-t border-black/10 pt-3">
        <summary className="text-xs font-mono-shelv uppercase tracking-widest text-[#0D9488] cursor-pointer">How to use this token</summary>
        <div className="mt-2 text-xs text-[#4A4D57] space-y-2">
          <p>Send all API calls to <code className="bg-[#F5F6F8] px-1 py-0.5 font-mono-shelv">https://reyanshcrm.com/api</code> with header:</p>
          <pre className="bg-[#0A0A0A] text-[#F5F6F8] p-3 font-mono-shelv text-[11px] overflow-x-auto">Authorization: Bearer YOUR_TOKEN_HERE</pre>
          <p>Example — push a new lead from your website:</p>
          <pre className="bg-[#0A0A0A] text-[#F5F6F8] p-3 font-mono-shelv text-[11px] overflow-x-auto whitespace-pre">{`curl -X POST https://reyanshcrm.com/api/leads \\
  -H "Authorization: Bearer YOUR_TOKEN_HERE" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Rajesh Kumar",
    "company": "ABC Industries",
    "email": "rajesh@abc.in",
    "phone": "+919876543210",
    "location": "Mumbai",
    "source": "Website",
    "requirement": "Oxygen plant for 50-bed hospital",
    "value": 0
  }'`}</pre>
          <p className="text-[11px] text-[#717680]">Full reference shared in chat — ask the developer to follow the &quot;Integration Guide&quot; the admin provides.</p>
        </div>
      </details>
    </div>
  );
}

// ============ END API TOKENS PANEL ============

// PLACEHOLDER_FOR_GMAIL_PANEL_BELOW

// ============ GMAIL CONNECT PANEL ============
function GmailConnectPanel() {
  const [status, setStatus] = useState(null); // null=loading, {connected, email}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/oauth/gmail/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  };
  useEffect(() => {
    load();
    // Show toast based on ?gmail=connected | ?gmail=error&reason=... query
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("gmail") === "connected") {
      toast.success("Gmail connected successfully");
      window.history.replaceState({}, "", window.location.pathname);
      // Re-fetch status
      setTimeout(load, 400);
    } else if (sp.get("gmail") === "error") {
      toast.error(`Gmail connect failed: ${sp.get("reason") || "unknown"}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/gmail/connect");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Gmail connect");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail? You'll need to re-authorize to send emails.")) return;
    setBusy(true);
    try {
      await api.post("/oauth/gmail/disconnect");
      toast.success("Gmail disconnected");
      await load();
    } catch (e) {
      toast.error("Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 border border-black/10 bg-white p-4 md:p-5" data-testid="gmail-connect-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
        <div>
          <div className="overline text-[#D90429] mb-1">📨 Gmail Workspace</div>
          <h3 className="font-display text-xl font-black tracking-tighter text-[#0A0A0A]">Send emails as you</h3>
        </div>
        {status?.connected ? (
          <button
            onClick={disconnect}
            disabled={busy}
            data-testid="gmail-disconnect-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#D90429] text-[#D90429] text-sm font-semibold hover:bg-[#D90429] hover:text-white disabled:opacity-40 transition"
          >
            {busy ? "Working…" : "Disconnect"}
          </button>
        ) : (
          <button
            onClick={connect}
            disabled={busy || status === null}
            data-testid="gmail-connect-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#D90429] text-white text-sm font-bold hover:bg-[#EF233C] disabled:opacity-40 transition"
          >
            {busy ? "Opening Google…" : "Connect Gmail"}
          </button>
        )}
      </div>

      <p className="text-sm text-[#4A4D57] max-w-2xl">
        Authorize Reyansh to send <b>Proposals, Quotations, Proforma &amp; Tax Invoices</b> on your behalf from your Shelvestech Gmail. Emails will show your real address as the sender, replies arrive in your inbox, and a copy lives in your Gmail Sent folder.
      </p>

      {status && (
        <div className="mt-4 flex items-center gap-3 text-sm" data-testid="gmail-status">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${status.connected ? "bg-[#0D9488]" : "bg-[#D90429]"}`} />
          {status.connected ? (
            <span>
              Connected as <b className="font-mono-shelv text-[#0A0A0A]">{status.email || "(unknown)"}</b>
              {status.scope_send === false && (
                <span className="ml-2 text-[#D90429] font-semibold">⚠ Send permission missing — reconnect.</span>
              )}
            </span>
          ) : (
            <span className="text-[#717680]">Not connected · click <b>Connect Gmail</b> above.</span>
          )}
        </div>
      )}
    </div>
  );
}

const WIPE_GROUPS = [
  { key: "leads",              label: "Leads" },
  { key: "products",           label: "Catalog · Products" },
  { key: "proposals",          label: "Proposals" },
  { key: "contacts",           label: "Finance · Clients & Vendors" },
  { key: "billing_docs",       label: "Finance · Billing (Quotation/Proforma/Tax)" },
  { key: "vouchers",           label: "Finance · Vouchers" },
  { key: "operating_expenses", label: "Finance · Operating Expenses" },
  { key: "work_orders",        label: "Finance · Work Orders" },
  { key: "employees",          label: "HR · Employees / Onboarding" },
  { key: "salaries",           label: "HR · Salary records" },
  { key: "hr_letters",         label: "HR · Letters" },
  { key: "resumes",            label: "HR · Resumes" },
  { key: "policies",           label: "Company Policies (shared)" },
  { key: "tracker_tasks",      label: "Shelves-Track tasks (shared)" },
  { key: "productivity_log",   label: "MIS · Daily Logs" },
  { key: "mis_entities",       label: "MIS · Billing Entities" },
];

function WipeDataPanel() {
  const [picked, setPicked] = useState(new Set());
  const [scope, setScope] = useState("mine"); // "mine" | "all"
  const [wiping, setWiping] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (k) => setPicked((s) => {
    const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const all = () => setPicked(new Set(WIPE_GROUPS.map((g) => g.key)));
  const none = () => setPicked(new Set());

  const wipe = async () => {
    if (picked.size === 0) { toast.error("Pick at least one collection"); return; }
    const list = WIPE_GROUPS.filter((g) => picked.has(g.key)).map((g) => g.label).join(", ");
    const scopeLabel = scope === "all" ? "EVERYONE'S data (entire workspace)" : "YOUR data";
    if (!window.confirm(`Permanently delete ${scopeLabel} from:\n\n${list}\n\nThis cannot be undone. Click OK to continue.`)) return;
    // Extra confirmation for workspace-wide wipe
    if (scope === "all") {
      const typed = window.prompt(`FINAL CHECK — you are about to wipe data for ALL users in this workspace.\n\nType DELETE ALL to confirm:`);
      if (typed !== "DELETE ALL") { toast.info("Wipe cancelled"); return; }
    }
    setWiping(true); setResult(null);
    try {
      const { data } = await api.post("/admin/wipe-data", { collections: [...picked], scope });
      setResult(data);
      toast.success(`Wiped ${data.total} records across ${Object.keys(data.deleted).length} collection(s)`);
      setPicked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setWiping(false); }
  };

  return (
    <div className="mt-12 border-2 border-[#D90429] bg-[#FFE5E9]/30 p-4 md:p-5" data-testid="wipe-panel">
      <div className="overline text-[#D90429] mb-1">⚠ Danger zone · Bulk delete</div>
      <h3 className="font-display text-xl font-black tracking-tighter text-[#0A0A0A]">Wipe test data</h3>
      <p className="text-sm text-[#4A4D57] mt-2 max-w-2xl">
        Pick collections below to delete in <b>one server-side call per collection</b> — vastly faster than clicking trash icons one by one. Policies &amp; Shelves-Track tasks are shared workspace data and always wipe for everyone.
      </p>

      {/* Scope selector */}
      <div className="mt-4 border border-black/10 bg-white p-3" data-testid="wipe-scope-selector">
        <div className="overline mb-2">Scope</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className={`flex items-start gap-2 cursor-pointer border p-3 transition ${scope === "mine" ? "border-[#D90429] bg-[#FFE5E9]/40" : "border-black/15 hover:border-black/30"}`} data-testid="wipe-scope-mine">
            <input type="radio" name="wipe-scope" value="mine" checked={scope === "mine"} onChange={() => setScope("mine")} className="accent-[#D90429] mt-0.5" />
            <div className="text-xs">
              <div className="font-bold text-[#0A0A0A]">Only my data</div>
              <div className="text-[#717680] mt-0.5">Records owned by your user account ({"<"}user_id{">"}).</div>
            </div>
          </label>
          <label className={`flex items-start gap-2 cursor-pointer border p-3 transition ${scope === "all" ? "border-[#D90429] bg-[#FFE5E9]/40" : "border-black/15 hover:border-black/30"}`} data-testid="wipe-scope-all">
            <input type="radio" name="wipe-scope" value="all" checked={scope === "all"} onChange={() => setScope("all")} className="accent-[#D90429] mt-0.5" />
            <div className="text-xs">
              <div className="font-bold text-[#D90429]">ALL workspace data <span className="ml-1 text-[9px] tracking-widest uppercase border border-[#D90429] px-1 py-0.5">High risk</span></div>
              <div className="text-[#717680] mt-0.5">Every record in the picked collections, regardless of which user uploaded it. Use to clear test data uploaded under other users / public viewers.</div>
            </div>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
        {WIPE_GROUPS.map((g) => (
          <label key={g.key} className="flex items-center gap-2 text-sm cursor-pointer hover:text-[#D90429]" data-testid={`wipe-${g.key}`}>
            <input type="checkbox" checked={picked.has(g.key)} onChange={() => toggle(g.key)} className="accent-[#D90429] w-4 h-4" />
            {g.label}
          </label>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <button onClick={all} className="text-xs px-3 py-1.5 border border-black/15 hover:bg-[#0A0A0A] hover:text-white" data-testid="wipe-select-all">Select all</button>
        <button onClick={none} className="text-xs px-3 py-1.5 border border-black/15 hover:bg-[#0A0A0A] hover:text-white" data-testid="wipe-clear">Clear</button>
        <div className="flex-1" />
        <button onClick={wipe} disabled={wiping || picked.size===0} data-testid="wipe-submit" className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-bold disabled:opacity-40 ${scope === "all" ? "bg-[#0A0A0A] hover:bg-[#D90429]" : "bg-[#D90429] hover:bg-[#EF233C]"}`}>
          {wiping ? "Wiping…" : `${scope === "all" ? "WIPE ALL" : "Delete"} ${picked.size} module${picked.size===1?"":"s"}`}
        </button>
      </div>
      {result && (
        <div className="mt-4 border border-black/10 bg-white p-3 text-sm" data-testid="wipe-result">
          <div className="font-bold text-[#D90429] mb-1">
            Wipe complete · {result.total} records removed
            <span className="ml-2 text-[10px] font-mono-shelv tracking-widest uppercase text-[#717680]">(scope: {result.scope})</span>
          </div>
          <ul className="text-xs text-[#4A4D57]">
            {Object.entries(result.deleted).map(([k,v]) => <li key={k}>· {k}: {v}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
